import React, { useState, useEffect, memo, useRef, useCallback, useMemo } from 'react';
import { Dimensions, Animated, Pressable, Platform } from 'react-native';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';
import { changeWidget } from '@reduxLocal/reducers/widgetReducer';
import { BlurView, Text, View } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import * as Animatable from 'react-native-animatable';
import { uniqueId } from 'lodash';
const { width } = Dimensions.get('window');

interface SpinnerProps {
  components: any[],
  onSwipe?: Function,
  open?: boolean
}

// Memoized individual spinner item for better performance
const SpinnerItem = memo(({
  item,
  index,
  translateX,
  translateY,
  isActive,
  shouldGreyOut,
  open,
  itemRotation,
  itemStyle,
  onPress
}: any) => {
  return (
    <Animated.View
      key={index}
      shouldRasterizeIOS={true}
      renderToHardwareTextureAndroid={true}
      style={[
        itemStyle,
        {
          top: -40,
          zIndex: 10,
          transform: [
            { translateX },
            { translateY },
            { scale: !open ? (isActive ? 0.9 : 0.6) : 1 },
            { rotate: itemRotation },
          ],
        }
      ]}
    >
      <Pressable
        disabled={!open}
        onPress={onPress}
        style={!open ? {
          top: isActive ? 20 : -60,
        } : undefined}
      >
        <View style={{ opacity: shouldGreyOut ? 0.4 : 1 }}>
          {item.smallWidget}
        </View>
        {open && (
          <Animatable.Text
            animation={'fadeIn'}
            duration={200}
            numberOfLines={1}
            useNativeDriver={true}
            style={{
              color: 'black',
              textAlign: 'center',
              fontSize: 13,
              backgroundColor: 'white',
              padding: 4,
              borderRadius: 5,
              overflow: 'hidden',
              maxWidth: 75,
              paddingHorizontal: 5,
              alignSelf: 'center',
              top: 90,
              fontWeight: 'bold',
            }}
          >
            {item.label}
          </Animatable.Text>
        )}
      </Pressable>
    </Animated.View>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo optimization
  return (
    prevProps.index === nextProps.index &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.open === nextProps.open &&
    prevProps.shouldGreyOut === nextProps.shouldGreyOut &&
    prevProps.item.id === nextProps.item.id
  );
});

const SpinnerCircle: React.FC<SpinnerProps> = ({ components, onSwipe, open }) => {
  const componentCount = components.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const animation = useRef(new Animated.Value(0)).current;
  const dispatch = useDispatch();
  const theme = themeSelector();
  const activeIndexRef = useRef(0);
  const translationX = useRef(new Animated.Value(0)).current;
  const dragStartRotation = useRef(0);
  const isAnimatingRef = useRef(false);

  // Memoize angle calculation
  const itemAngle = useMemo(() => 360 / componentCount, [componentCount]);

  useEffect(() => {
    const initWidgetId = store.getState().widgets.activeWidget.id;
    const initIndex = components.findIndex(comp => comp.id === initWidgetId);
    if (initIndex >= 0) {
      const newRotation = -initIndex * itemAngle;
      animation.setValue(newRotation);
      setActiveIndex(initIndex);
      activeIndexRef.current = initIndex;
      dragStartRotation.current = newRotation;
    }
  }, []);

  // Sync with external widget changes from Redux
  const currentWidgetId = useSelector((state: DefaultRootState) => state.widgets.activeWidget.id);
  useEffect(() => {
    const widgetIndex = components.findIndex(comp => comp.id === currentWidgetId);
    if (widgetIndex !== -1 && widgetIndex !== activeIndex) {
      setActiveIndex(widgetIndex);
      activeIndexRef.current = widgetIndex;
    }
  }, [currentWidgetId, components, activeIndex]);

  // Optimized select handler - removed debounce for instant response
  const _handleSelect = useCallback((widget, index) => {
    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    activeIndexRef.current = index;
    setActiveIndex(index);

    const targetRotation = -index * itemAngle;
    dragStartRotation.current = targetRotation;

    Animated.spring(animation, {
      toValue: targetRotation,
      useNativeDriver: true,
      tension: 65,
      friction: 8,
    }).start(() => {
      isAnimatingRef.current = false;
      dispatch(changeWidget({ id: widget.id }));
    });
  }, [animation, itemAngle, dispatch]);

  const onGestureEvent = Animated.event(
    [{ nativeEvent: { translationX: translationX } }],
    { useNativeDriver: true }
  );

  const onHandlerStateChange = useCallback(({ nativeEvent }) => {
    if (nativeEvent.state === State.BEGAN) {
      dragStartRotation.current = (animation as any)._value || 0;
    } else if (nativeEvent.state === State.END || nativeEvent.state === State.CANCELLED) {
      const dragDegrees = (nativeEvent.translationX / (width * 0.8)) * 360;
      const finalRotation = dragStartRotation.current + dragDegrees;

      let newIndex = Math.round(-finalRotation / itemAngle);
      newIndex = ((newIndex % componentCount) + componentCount) % componentCount;

      translationX.setValue(0);
      _handleSelect(components[newIndex], newIndex);

      if (onSwipe) {
        onSwipe(newIndex);
      }
    }
  }, [componentCount, itemAngle, translationX, _handleSelect, components, onSwipe]);



  // Memoize static styles
  const discStyle = useMemo(() => ({
    maxWidth: 350,
    maxHeight: 330,
    borderRadius: 200,
    alignItems: 'center' as 'center',
    justifyContent: 'center' as 'center',
  }), []);

  const itemStyle = useMemo(() => ({
    position: 'absolute' as 'absolute',
    width: 80,
    height: 80,
    borderRadius: 30,
  }), []);

  const radius = 160;
  const safeComponentCount = componentCount > 0 ? componentCount : 1;

  // Memoize rotation animation
  const discRotation = useMemo(() =>
    animation.interpolate({
      inputRange: [-360 * safeComponentCount, 0, 360 * safeComponentCount],
      outputRange: [`-${360 * safeComponentCount}deg`, '0deg', `${360 * safeComponentCount}deg`],
    }), [animation, safeComponentCount]
  );

  // Memoize counter-rotation for items
  const itemRotation = useMemo(() =>
    Animated.multiply(animation, -1).interpolate({
      inputRange: [-360 * safeComponentCount, 0, 360 * safeComponentCount],
      outputRange: [
        `${-360 * safeComponentCount}deg`,
        `0deg`,
        `${360 * safeComponentCount}deg`
      ],
    }), [animation, safeComponentCount]
  );

  return (
    <View style={{ flex: 1, alignItems: 'center', overflow: 'visible' }}>
      <View
        style={{
          width: width,
          height: width,
          justifyContent: 'center',
          alignItems: 'center',
          left: -20,
          overflow: 'visible',
          borderRadius: 200,
          transform: [{ scale: 0.8 }]
        }}
      >
        {open && (
          <>
            <BlurView
              blurAmount={40}
              blurType='light'
              style={{
                top: -20,
                flex: 1,
                position: 'absolute',
                width: width,
                height: width,
                overflow: Platform.select({ android: "visible", default: "hidden" }),
                borderRadius: 210,
                zIndex: -1,
                backgroundColor: Platform.select({android: 'rgba(255, 255, 255, 0)', default: 'transparent'}),
                borderWidth: 1.5,
                borderColor: 'rgba(255, 255, 255, 0.6)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.1,
                shadowRadius: 12,
                // elevation: 5,
              }}
              reducedTransparencyFallbackColor="rgba(255, 255, 255, 0.85)"
            />
          </>
        )}
        <PanGestureHandler enabled={false} onGestureEvent={onGestureEvent} onHandlerStateChange={onHandlerStateChange}>
          <Animated.View
            shouldRasterizeIOS={true}
            renderToHardwareTextureAndroid={true}
            style={{
              ...discStyle,
              overflow: 'visible',
              borderWidth: 200,
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              borderRadius: 200,
              flex: 1,
              top: -20,
              transform: [
                { rotate: discRotation },
                { scale: Platform.select({ android: 1, default: 1 }) },
                { perspective: 1000 },
              ],
            }}
          >
            {
              components.map((item, index) => {
                const angle = ((360 / componentCount) * index * Math.PI) / 180;
                const translateX = radius * Math.sin(angle);
                const translateY = -radius * Math.cos(angle);

                const isActive = activeIndex === index;
                const shouldGreyOut = !open && !isActive;

                return (
                  <SpinnerItem
                    key={uniqueId(`${item.id}-${index}`)}
                    item={item}
                    index={index}
                    translateX={translateX}
                    translateY={translateY}
                    isActive={isActive}
                    shouldGreyOut={shouldGreyOut}
                    open={open}
                    itemRotation={itemRotation}
                    itemStyle={itemStyle}
                    onPress={() => _handleSelect(item, index)}
                  />
                );
              })
            }
          </Animated.View>
        </PanGestureHandler>
      </View>
      {
        !open &&
        <View style={{ borderRadius: 200, left: "0%", zIndex: -1, position: "absolute", bottom: Platform.select({ ios: 5, android: 15 }) as number, backgroundColor: theme ? "white" : "rgb(75, 156, 190)", shadowRadius: 20, shadowOpacity: .3, minWidth: "90%", minHeight: 140, maxHeight: Platform.select({ ios: 350, android: 300 }) as number }} />
      }
    </View>
  );
};

export default memo(SpinnerCircle);